import classes from './ProductForm.module.css';
import useForm from '../../../hooks/useForm';
import Button from '../../UI/Button/Button';
import Card from '../../UI/Card/Card';
import Form from '../../UI/Form/Form';
import Input from '../../UI/Input/Input';
import Textarea from '../../UI/Textarea/Textarea';
import { FillerType, FabricType, Category, Product, PromoKey } from '../../../types/common';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { createProduct, updateProduct } from '../../../store/ProductSlice';
import { fetchProdGroups } from '../../../store/ProdGroupSlice';
import ProductFormSelect from './ProductFormSelect/ProductFormSelect';
import { productFormValidator } from '../../../utils/validators';
import { priorityOptions } from '../../../utils/helpers';
import { ARTICLE_DUPLICATE_ERROR_MESSAGE } from '../../../constants/messages';
import { getCsrfToken } from '../../../utils/helpers';

const MAX_DISCOUNT = 30;
const MAX_SPECIAL_DISCOUNT = 100;

const seasonOptions = [
    // { id: '', name: 'Всесезонный' },
    { id: 'summer', name: 'Летний' },
    { id: 'autumn', name: 'Осенний' },
    { id: 'winter', name: 'Зимний' },
    { id: 'spring', name: 'Весенний' },
];

const promoTypes: { id: '' | PromoKey; name: string }[] = [
    { id: '', name: 'Нет' },
    { id: 'promo', name: 'Промо' },
    { id: 'season', name: 'Сезонная' },
    { id: 'sale', name: 'Распродажа' },
    { id: 'blank', name: 'Акция' },
];

const reviewsEnabledOptions = [
    { id: 'true', name: 'Включены' },
    { id: 'false', name: 'Отключены' },
];

const INIT_INPUT = {
    name: '',
    nameDescription: '',
    complectation: '',
    description: '',
    shortDescription: '',
    price: '',
    weight: '',
    grossWeight: '',
    length: '',
    width: '',
    height: '',
    fillerType: {
        id: '',
        name: '',
        url: '',
    },
    fabricType: {
        id: '',
        name: '',
        url: '',
    },
    discount: '',
    specialDiscount: '',
    category: {
        id: '',
        name: '',
        url: '',
    },
    group: { id: '', name: '', url: '', optionValue: '' },
    quantity: '',
    image: null as File | null,
    secondImage: null as File | null,
    thirdImage: null as File | null,
    fourthImage: null as File | null,
    fifthImage: null as File | null,
    sixthImage: null as File | null,
    brandName: '',
    rawMaterial: '',
    careInstructions: '',
    countryOrigin: '',
    sellerName: '',
    articleNumber: '',
    productСolor: '',
    priority: '10',
    season: '',
    promoType: { id: '', name: 'Нет' },
    reviewsEnabled: true,
};

interface IProductFormProps {
    onClose: () => void;
    categories: Category[];
    typesOfFiller: FillerType[];
    typesOfFabric: FabricType[];
    // groups: ProdGroup[];
}

const ProductForm: React.FC<IProductFormProps> = ({ onClose, categories, typesOfFiller, typesOfFabric }) => {
    const { input, setInput, handleChange, errors, submit, handleChangeSelect } = useForm(INIT_INPUT, submitHandler, productFormValidator);
    const dispatch = useDispatch<AppDispatch>();
    const { isLoading: areGroupsLoading, groups: groupsFromStore } = useSelector((s: RootState) => s.prodGroup);
    const productToBeEdited = useSelector((state: RootState) => state.product.selectedProduct);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const secondFileInputRef = useRef<HTMLInputElement>(null);
    const thirdFileInputRef = useRef<HTMLInputElement>(null);
    const fourthFileInputRef = useRef<HTMLInputElement>(null);
    const fifthFileInputRef = useRef<HTMLInputElement>(null);
    const sixthFileInputRef = useRef<HTMLInputElement>(null);
    const articleRef = useRef<HTMLInputElement>(null);
    const initIdRef = useRef<string | null>(null);
    const prevMainUrlRef = useRef<string | null>(null);
    const [previewMain, setPreviewMain] = useState<string>('');
    const [previewSecond, setPreviewSecond] = useState<string>('');
    const [previewThird, setPreviewThird] = useState<string>('');
    const [previewFourth, setPreviewFourth] = useState<string>('');
    const [previewFifth, setPreviewFifth] = useState<string>('');
    const [previewSixth, setPreviewSixth] = useState<string>('');
    const [deleteSecond, setDeleteSecond] = useState(false);
    const [deleteThird, setDeleteThird] = useState(false);
    const [deleteFourth, setDeleteFourth] = useState(false);
    const [deleteFifth, setDeleteFifth] = useState(false);
    const [deleteSixth, setDeleteSixth] = useState(false);

    const handleShortDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = (e.target.value || '').slice(0, 81);
        setInput((prev) => ({ ...prev, shortDescription: value }));
    };

    useEffect(() => {
        const anyEdited = productToBeEdited as any;
        const initKey: string | null =
            (productToBeEdited.id ? String(productToBeEdited.id) : null) || (typeof anyEdited?.__draftKey === 'string' ? anyEdited.__draftKey : null) || null;
        const hasPrefillData = Boolean(String(productToBeEdited.name).trim());

        // const id = productToBeEdited.id ?? null;
        if (!initKey && !hasPrefillData) {
            initIdRef.current = null;
            setInput(() => ({
                ...INIT_INPUT,
                fillerType: { ...INIT_INPUT.fillerType },
                fabricType: { ...INIT_INPUT.fabricType },
                category: { ...INIT_INPUT.category },
                group: { ...INIT_INPUT.group },
                promoType: { ...INIT_INPUT.promoType },
            }));
            setDeleteSecond(false);
            setDeleteThird(false);
            setDeleteFourth(false);
            setDeleteFifth(false);
            setDeleteSixth(false);
            setPreviewMain('');
            setPreviewSecond('');
            setPreviewThird('');
            setPreviewFourth('');
            setPreviewFifth('');
            setPreviewSixth('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (secondFileInputRef.current) secondFileInputRef.current.value = '';
            if (thirdFileInputRef.current) thirdFileInputRef.current.value = '';
            if (fourthFileInputRef.current) fourthFileInputRef.current.value = '';
            if (fifthFileInputRef.current) fifthFileInputRef.current.value = '';
            if (sixthFileInputRef.current) sixthFileInputRef.current.value = '';
            return;
        }
        if (!hasPrefillData) return;
        if (initIdRef.current === initKey) return;
        initIdRef.current = initKey;
        // if (!productToBeEdited.name) return;
        // if (initIdRef.current === id) return;
        // initIdRef.current = id;
        const editedPromoOpt = promoTypes.find((o) => o.id === (productToBeEdited as any).promoType) || { id: '', name: 'Нет' };
        const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));
        setInput((prevState) => ({
            ...prevState,
            ...productToBeEdited,
            discount: productToBeEdited.discount ? productToBeEdited.discount?.percent.toString() : '',
            specialDiscount: productToBeEdited.specialDiscount ? productToBeEdited.specialDiscount.percent.toString() : '',
            grossWeight: toStr(productToBeEdited.grossWeight),
            price: toStr(productToBeEdited.price),
            quantity: toStr(productToBeEdited.quantity),
            weight: toStr(productToBeEdited.weight),
            image: productToBeEdited.image,
            rawMaterial: productToBeEdited.rawMaterial || '',
            careInstructions: productToBeEdited.careInstructions || '',
            brandName: productToBeEdited.brandName || '',
            countryOrigin: productToBeEdited.countryOrigin || '',
            sellerName: productToBeEdited.sellerName || '',
            articleNumber: productToBeEdited.articleNumber || '',
            productСolor: productToBeEdited.productСolor || '',
            nameDescription: productToBeEdited.nameDescription || '',
            complectation: productToBeEdited.complectation || '',
            priority: productToBeEdited.priority?.toString() || '10',
            length: productToBeEdited.dimensions?.length?.toString() || '',
            width: productToBeEdited.dimensions?.width?.toString() || '',
            height: productToBeEdited.dimensions?.height?.toString() || '',
            fillerType: productToBeEdited.fillerType
                ? { id: productToBeEdited.fillerType.id, name: productToBeEdited.fillerType.name, url: productToBeEdited.fillerType.url || '' }
                : { id: '', name: '', url: '' },
            fabricType: productToBeEdited.fabricType
                ? { id: productToBeEdited.fabricType.id, name: productToBeEdited.fabricType.name, url: productToBeEdited.fabricType.url || '' }
                : { id: '', name: '', url: '' },
            group: productToBeEdited.group ?? { id: '', name: '', url: '', optionValue: '' },
            season: productToBeEdited.season ?? '',
            promoType: editedPromoOpt,
            shortDescription: productToBeEdited.shortDescription || '',
            reviewsEnabled: (productToBeEdited as any).reviewsEnabled === false ? false : true,
        }));
        setDeleteSecond(false);
        setDeleteThird(false);
        setDeleteFourth(false);
        setDeleteFifth(false);
        setDeleteSixth(false);
        const base = process.env.REACT_APP_SERVER_URL || '';
        // setPreviewMain(productToBeEdited.image ? `${base}/products/images/${productToBeEdited.image}` : '');
        // setPreviewSecond(productToBeEdited.secondImage ? `${base}/products/images/${productToBeEdited.secondImage}` : '');
        // setPreviewThird(productToBeEdited.thirdImage ? `${base}/products/images/${productToBeEdited.thirdImage}` : '');
        // setPreviewFourth(productToBeEdited.fourthImage ? `${base}/products/images/${productToBeEdited.fourthImage}` : '');
        // setPreviewFifth(productToBeEdited.fifthImage ? `${base}/products/images/${productToBeEdited.fifthImage}` : '');
        // setPreviewSixth(productToBeEdited.sixthImage ? `${base}/products/images/${productToBeEdited.sixthImage}` : '');
        if (Boolean(productToBeEdited.id)) {
            setPreviewMain(productToBeEdited.image ? `${base}/products/images/${productToBeEdited.image}` : '');
            setPreviewSecond(productToBeEdited.secondImage ? `${base}/products/images/${productToBeEdited.secondImage}` : '');
            setPreviewThird(productToBeEdited.thirdImage ? `${base}/products/images/${productToBeEdited.thirdImage}` : '');
            setPreviewFourth(productToBeEdited.fourthImage ? `${base}/products/images/${productToBeEdited.fourthImage}` : '');
            setPreviewFifth(productToBeEdited.fifthImage ? `${base}/products/images/${productToBeEdited.fifthImage}` : '');
            setPreviewSixth(productToBeEdited.sixthImage ? `${base}/products/images/${productToBeEdited.sixthImage}` : '');
        } else {
            setPreviewMain('');
            setPreviewSecond('');
            setPreviewThird('');
            setPreviewFourth('');
            setPreviewFifth('');
            setPreviewSixth('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (secondFileInputRef.current) secondFileInputRef.current.value = '';
            if (thirdFileInputRef.current) thirdFileInputRef.current.value = '';
            if (fourthFileInputRef.current) fourthFileInputRef.current.value = '';
            if (fifthFileInputRef.current) fifthFileInputRef.current.value = '';
            if (sixthFileInputRef.current) sixthFileInputRef.current.value = '';
        }
    }, [productToBeEdited, setInput]);

    useEffect(() => {
        if (!groupsFromStore.length) {
            dispatch(fetchProdGroups());
        }
    }, [groupsFromStore.length, dispatch]);

    const handleSeasonSelect = (option: { [key: string]: string }) => {
        setInput((p) => ({ ...p, season: option.id }));
    };

    const handleMainFileChange = () => {
        const file = fileInputRef.current?.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (prevMainUrlRef.current) URL.revokeObjectURL(prevMainUrlRef.current);
        prevMainUrlRef.current = url;
        setPreviewMain(url);
        setInput((prev) => ({ ...prev, image: file }));
    };
    useEffect(() => {
        return () => {
            if (prevMainUrlRef.current) URL.revokeObjectURL(prevMainUrlRef.current);
        };
    }, []);
    const handleSecondFileChange = () => {
        const file = secondFileInputRef.current?.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewSecond(url);
            setDeleteSecond(false);
            setInput((prev) => ({
                ...prev,
                secondImage: file,
            }));
        }
    };
    const handleThirdFileChange = () => {
        const file = thirdFileInputRef.current?.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewThird(url);
            setDeleteThird(false);
            setInput((prev) => ({
                ...prev,
                thirdImage: file,
            }));
        }
    };
    const handleFourthFileChange = () => {
        const file = fourthFileInputRef.current?.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewFourth(url);
            setDeleteFourth(false);
            setInput((prev) => ({
                ...prev,
                fourthImage: file,
            }));
        }
    };
    const handleFifthFileChange = () => {
        const file = fifthFileInputRef.current?.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewFifth(url);
            setDeleteFifth(false);
            setInput((prev) => ({
                ...prev,
                fifthImage: file,
            }));
        }
    };
    const handleSixthFileChange = () => {
        const file = sixthFileInputRef.current?.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewSixth(url);
            setDeleteSixth(false);
            setInput((prev) => ({
                ...prev,
                sixthImage: file,
            }));
        }
    };

    function handleDeleteSecond() {
        setPreviewSecond('');
        setDeleteSecond(true);
        if (secondFileInputRef.current) secondFileInputRef.current.value = '';
    }
    function handleDeleteThird() {
        setPreviewThird('');
        setDeleteThird(true);
        if (thirdFileInputRef.current) thirdFileInputRef.current.value = '';
    }
    function handleDeleteFourth() {
        setPreviewFourth('');
        setDeleteFourth(true);
        if (fourthFileInputRef.current) fourthFileInputRef.current.value = '';
    }
    function handleDeleteFifth() {
        setPreviewFifth('');
        setDeleteFifth(true);
        if (fifthFileInputRef.current) fifthFileInputRef.current.value = '';
    }
    function handleDeleteSixth() {
        setPreviewSixth('');
        setDeleteSixth(true);
        if (sixthFileInputRef.current) sixthFileInputRef.current.value = '';
    }

    const handlePrioritySelect = (option: { [key: string]: string }) => {
        setInput((prev) => ({ ...prev, priority: option.id }));
    };

    const handleChangeDim = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (value.trim() === '') {
            setInput((prev) => ({ ...prev, [name]: '' }));
            return;
        }
        const norm = value.replace(',', '.');
        const num = Number(norm);
        if (!Number.isFinite(num) || num < 0 || num > 5000) return;
        setInput((prev) => ({ ...prev, [name]: value }));
    };

    const handleGroupSelect = (option: { [key: string]: string }) => {
        setInput((p) => ({
            ...p,
            group: option.id ? { id: option.id, name: option.name, url: option.url, optionValue: '' } : { id: '', name: '', url: '', optionValue: '' },
        }));
    };

    const handleClose = () => {
        initIdRef.current = null;
        onClose();
    };

    const isEditing = Boolean(productToBeEdited.id);

    const crptAvailable = (() => {
        const raw = (productToBeEdited as any)?.crptAvailable;
        if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
        if (typeof raw === 'string') {
            const n = Number(raw);
            return Number.isFinite(n) ? n : 0;
        }
        if (raw && typeof raw === 'object') {
            const anyRaw = raw as any;
            if (typeof anyRaw.$numberInt === 'string') {
                const n = Number(anyRaw.$numberInt);
                return Number.isFinite(n) ? n : 0;
            }
            if (typeof anyRaw.value === 'number' && Number.isFinite(anyRaw.value)) return anyRaw.value;
            if (typeof anyRaw.value === 'string') {
                const n = Number(anyRaw.value);
                return Number.isFinite(n) ? n : 0;
            }
        }
        return 0;
    })();

    async function submitHandler() {
        const price = Math.max(0, Math.round(Number(input.price) || 0));
        const parseNum = (raw: string): number => {
            const s = (raw ?? '').trim().replace(',', '.');
            if (s === '') return 0;
            const n = Number(s);
            if (!Number.isFinite(n)) return 0;
            return n;
        };
        const prevWeight = Number(productToBeEdited.weight) || 0;
        const weight = isEditing && input.weight.trim() === '' ? prevWeight : parseNum(input.weight);
        const prevGrossWeight = Number(productToBeEdited.grossWeight) || 0;
        const prevQuantity = Number(productToBeEdited.quantity) || 0;
        const grossWeight = isEditing && input.grossWeight.trim() === '' ? prevGrossWeight : parseNum(input.grossWeight);
        const quantity = isEditing && input.quantity.trim() === '' ? prevQuantity : Number(input.quantity) || 0;
        let discount: Product['discount'] = null;
        let specialDiscount: Product['specialDiscount'] = null;

        if (+input.discount) {
            const percent = Math.ceil(+input.discount);
            const discountedPrice = price - Math.round((price * percent) / 100);
            discount = {
                percent,
                discountedPrice,
            };
        }
        if (+input.specialDiscount) {
            const spPercent = Math.ceil(+input.specialDiscount);
            const spDiscountedPrice = price - Math.round((price * spPercent) / 100);
            specialDiscount = {
                percent: spPercent,
                discountedPrice: spDiscountedPrice,
            };
        }
        const priority = +input.priority >= 1 && +input.priority <= 10 ? +input.priority : 10;
        const file = fileInputRef.current?.files?.[0];
        const secondFile = secondFileInputRef.current?.files?.[0];
        const thirdFile = thirdFileInputRef.current?.files?.[0];
        const fourthFile = fourthFileInputRef.current?.files?.[0];
        const fifthFile = fifthFileInputRef.current?.files?.[0];
        const sixthFile = sixthFileInputRef.current?.files?.[0];
        const shortDescription = (input.shortDescription || '').trim().slice(0, 81);

        const { complectation, length, width, height, ...restInput } = input;
        const parseDim = (raw: string): number => {
            const s = (raw ?? '').trim().replace(',', '.');
            if (s === '') return 0;
            const n = Number(s);
            if (!Number.isFinite(n)) return 0;
            return Math.max(0, Math.min(5000, n));
        };
        // const isEditing = Boolean(productToBeEdited.id);
        const prevDims = productToBeEdited.dimensions ?? { length: 0, width: 0, height: 0 };
        const dimensions = {
            length: isEditing && length.trim() === '' ? prevDims.length : parseDim(length),
            width: isEditing && width.trim() === '' ? prevDims.width : parseDim(width),
            height: isEditing && height.trim() === '' ? prevDims.height : parseDim(height),
        };

        const group = input.group.id ? input.group : null;
        const season = input.season ? input.season : null;
        const promoType: Product['promoType'] = input.promoType && input.promoType.id ? (input.promoType.id as PromoKey) : null;
        const normalizedFabricType = input.fabricType?.id ? input.fabricType : null;
        const normalizedFillerType = input.fillerType?.id ? input.fillerType : null;

        try {
            if (productToBeEdited.id) {
                const updatedProduct = {
                    ...productToBeEdited,
                    ...restInput,
                    price,
                    discount,
                    specialDiscount,
                    weight,
                    grossWeight,
                    file,
                    quantity,
                    secondFile,
                    thirdFile,
                    fourthFile,
                    fifthFile,
                    sixthFile,
                    rawMaterial: input.rawMaterial,
                    careInstructions: input.careInstructions,
                    brandName: input.brandName,
                    countryOrigin: input.countryOrigin,
                    sellerName: input.sellerName,
                    articleNumber: input.articleNumber,
                    productСolor: input.productСolor,
                    nameDescription: input.nameDescription,
                    priority,
                    deleteSecond,
                    deleteThird,
                    deleteFourth,
                    deleteFifth,
                    deleteSixth,
                    dimensions,
                    complectation,
                    group,
                    season,
                    promoType,
                    shortDescription,
                    fabricType: normalizedFabricType,
                    fillerType: normalizedFillerType,
                    reviewsEnabled: Boolean((input as any).reviewsEnabled),
                };

                await dispatch(updateProduct(updatedProduct)).unwrap();
                const prevGroupId = productToBeEdited.group?.id || '';
                if (prevGroupId && !group) {
                    const csrf = await getCsrfToken();
                    await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/product-groups/${prevGroupId}/products`, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                        body: JSON.stringify({ productId: updatedProduct.id, action: 'remove' }),
                    });
                } else if (group && group.id && group.id !== prevGroupId) {
                    const csrf = await getCsrfToken();
                    await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/product-groups/${group.id}/products`, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                        body: JSON.stringify({ productId: updatedProduct.id }),
                    });
                }
            } else {
                const newProduct = {
                    category: input.category,
                    description: input.description,
                    discount,
                    specialDiscount,
                    name: input.name,
                    nameDescription: input.nameDescription,
                    complectation,
                    price,
                    weight,
                    grossWeight,
                    fillerType: normalizedFillerType,
                    fabricType: normalizedFabricType,
                    quantity,
                    file,
                    secondFile,
                    thirdFile,
                    fourthFile,
                    fifthFile,
                    sixthFile,
                    rawMaterial: input.rawMaterial,
                    careInstructions: input.careInstructions,
                    brandName: input.brandName,
                    countryOrigin: input.countryOrigin,
                    sellerName: input.sellerName,
                    articleNumber: input.articleNumber,
                    productСolor: input.productСolor,
                    priority,
                    dimensions,
                    group,
                    season,
                    promoType,
                    shortDescription,
                    reviewsEnabled: Boolean((input as any).reviewsEnabled),
                };

                const created: Product = await dispatch(createProduct(newProduct)).unwrap();
                if (group && group.id) {
                    const csrf = await getCsrfToken();
                    await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/product-groups/${group.id}/products`, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                        body: JSON.stringify({ productId: created.id }),
                    });
                }
            }
            handleClose();
        } catch (err) {
            const message = typeof err === 'string' ? err : ((err as { message?: string })?.message ?? '');
            console.log('Ошибка:', message);
            if (message === ARTICLE_DUPLICATE_ERROR_MESSAGE) {
                articleRef.current?.focus();
            }
        }
    }

    const handleChangeDisc = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'discount') {
            const clamped = +value > MAX_DISCOUNT ? MAX_DISCOUNT.toString() : value;
            setInput((prev) => ({ ...prev, discount: clamped }));
            return;
        }
        if (name === 'specialDiscount') {
            const clamped = +value > MAX_SPECIAL_DISCOUNT ? MAX_SPECIAL_DISCOUNT.toString() : value;
            setInput((prev) => ({ ...prev, specialDiscount: clamped }));
            return;
        }
        handleChange(e);
    };

    return (
        <div className={classes.wrapper}>
            <Card fullWidth>
                <Form onSubmit={submit}>
                    <>
                        <div className={classes.container}>
                            <ProductFormSelect
                                label="Группа"
                                defaultOptionText={areGroupsLoading ? 'Загрузка…' : 'Без группы'}
                                isDisabled={areGroupsLoading || !groupsFromStore?.length}
                                options={groupsFromStore ?? []}
                                required={false}
                                onSelect={handleGroupSelect}
                                value={input.group.name || ''}
                                errorText={errors.group}
                                field="group"
                            />
                            {input.group.id && (
                                <Input
                                    label={input.group.url}
                                    errorText={errors.optionValue}
                                    name="optionValue"
                                    type="text"
                                    value={input.group.optionValue || ''}
                                    onChange={(e) =>
                                        setInput((p) => ({
                                            ...p,
                                            group: { ...p.group, optionValue: e.target.value },
                                        }))
                                    }
                                    placeholder={`Укажите ${input.group.url.toLowerCase()}`}
                                />
                            )}
                            <ProductFormSelect
                                label={'Сезонность'}
                                defaultOptionText={'Всесезонный'}
                                isDisabled={false}
                                options={seasonOptions}
                                required={false}
                                onSelect={handleSeasonSelect}
                                value={seasonOptions.find((o) => o.id === input.season)?.name || ''}
                                errorText={errors.season}
                                field={'season'}
                            />
                            <ProductFormSelect
                                label={'Отзывы к товару'}
                                defaultOptionText={'Выберите состояние'}
                                isDisabled={false}
                                options={reviewsEnabledOptions}
                                onSelect={(opt: { [key: string]: string }) => setInput((p) => ({ ...p, reviewsEnabled: opt.id === 'true' }))}
                                value={(input as any).reviewsEnabled ? 'Включены' : 'Отключены'}
                                field={'reviewsEnabled'}
                            />
                        </div>

                        <Input
                            label={'Название товара'}
                            errorText={errors.name}
                            name={'name'}
                            type={'text'}
                            value={input.name || ''}
                            onChange={handleChange}
                            required
                            placeholder={'Укажите название категории'}
                        />

                        <Input
                            label={'Детали названия'}
                            errorText={errors.nameDescription}
                            name={'nameDescription'}
                            type={'text'}
                            value={input.nameDescription || ''}
                            onChange={handleChange}
                            placeholder={'Например, модель, оттенок, коллекция'}
                        />

                        <Input
                            label={'Комплектация'}
                            errorText={errors.complectation}
                            name={'complectation'}
                            type={'text'}
                            value={input.complectation || ''}
                            onChange={handleChange}
                            placeholder={'Например, товар, инструкция, чехол'}
                        />

                        <Textarea
                            label={'Описание'}
                            errorText={errors.description}
                            name={'description'}
                            placeholder={'Заполните описание категории'}
                            onChange={handleChange}
                            value={input.description || ''}
                            maxLength={12000}
                            rows={15}
                            enableParagraphOnShiftEnter
                        />

                        <Textarea
                            label={'Краткое описание для фидов'}
                            errorText={errors.shortDescription}
                            name={'shortDescription'}
                            placeholder={'Необязательное поле, до 81 символа'}
                            onChange={handleShortDescriptionChange}
                            value={input.shortDescription || ''}
                            rows={2}
                        />

                        <div className={classes.container}>
                            <Textarea
                                label={'Состав'}
                                errorText={errors.rawMaterial}
                                name={'rawMaterial'}
                                placeholder={'Заполните состав товара'}
                                onChange={handleChange}
                                value={input.rawMaterial || ''}
                            />

                            <Textarea
                                label={'Инструкция по уходу'}
                                errorText={errors.careInstructions}
                                name={'careInstructions'}
                                placeholder={'Заполните инструкцию по уходу.'}
                                onChange={handleChange}
                                value={input.careInstructions || ''}
                            />
                        </div>

                        <div className={classes.container}>
                            <Input
                                label={'Торговая марка'}
                                errorText={errors.brandName}
                                name={'brandName'}
                                type={'text'}
                                value={input.brandName || ''}
                                onChange={handleChange}
                                placeholder={''}
                            />

                            <Input
                                label={'Страна производства'}
                                errorText={errors.countryOrigin}
                                name={'countryOrigin'}
                                type={'text'}
                                value={input.countryOrigin || ''}
                                onChange={handleChange}
                                placeholder={''}
                            />

                            <Input
                                label={'Продавец'}
                                errorText={errors.sellerName}
                                name={'sellerName'}
                                type={'text'}
                                value={input.sellerName || ''}
                                onChange={handleChange}
                                placeholder={''}
                            />
                        </div>

                        <div className={classes.container}>
                            <ProductFormSelect
                                label={'Категория'}
                                defaultOptionText={'Выберите категорию'}
                                isDisabled={false}
                                options={categories}
                                required
                                onSelect={handleChangeSelect}
                                value={input.category.name || ''}
                                errorText={errors.category}
                                field={'category'}
                            />

                            <ProductFormSelect
                                label={'Тип ткани'}
                                defaultOptionText={'Выберите тип ткани'}
                                isDisabled={false}
                                options={typesOfFabric}
                                // required
                                onSelect={handleChangeSelect}
                                value={input.fabricType.name || ''}
                                errorText={errors.fabricType}
                                field={'fabricType'}
                            />

                            <ProductFormSelect
                                label={'Тип наполнителя'}
                                defaultOptionText={'Выберите тип наполнителя'}
                                isDisabled={false}
                                options={typesOfFiller}
                                // required
                                onSelect={handleChangeSelect}
                                value={input.fillerType.name || ''}
                                errorText={errors.fillerType}
                                field={'fillerType'}
                            />
                        </div>

                        <div className={classes.container}>
                            <Input
                                ref={articleRef}
                                label={'Артикул'}
                                errorText={errors.articleNumber}
                                name={'articleNumber'}
                                type={'text'}
                                value={input.articleNumber || ''}
                                onChange={handleChange}
                                required
                                placeholder={'Введите артикул'}
                            />

                            <Input
                                label={'Дизайн, цвет'}
                                errorText={errors.productСolor}
                                name={'productСolor'}
                                type={'text'}
                                value={input.productСolor || ''}
                                onChange={handleChange}
                                placeholder={''}
                            />

                            {/* <Input
                                label={'Количество'}
                                errorText={errors.quantity}
                                name={'quantity'}
                                type={'number'}
                                value={input.quantity || ''}
                                onChange={handleChange}
                                required
                                placeholder={'Количество товара'}
                                min={1}
                                max={999999}
                            /> */}
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Input
                                    label={'Количество'}
                                    errorText={errors.quantity}
                                    name={'quantity'}
                                    type={'number'}
                                    value={input.quantity || ''}
                                    onChange={handleChange}
                                    required
                                    placeholder={'Количество товара'}
                                    min={1}
                                    max={999999}
                                />
                                {isEditing && (
                                    <div style={{ marginTop: '4px', color: '#9aa0a6', fontSize: '12px', lineHeight: 1.2 }}>промаркировано: {crptAvailable}</div>
                                )}
                            </div>
                        </div>

                        <div className={`${classes.fieldWithPreview}`}>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Главная иллюстрация'}
                                    errorText={errors.image}
                                    name={'image'}
                                    type={'file'}
                                    ref={fileInputRef}
                                    onChange={handleMainFileChange}
                                    required
                                />
                                {previewMain && <img src={previewMain} alt="Предпросмотр главной иллюстрации" className={classes.preview} />}
                            </div>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Вторая иллюстрация'}
                                    errorText={errors.secondImage}
                                    name={'secondImage'}
                                    type={'file'}
                                    ref={secondFileInputRef}
                                    onChange={handleSecondFileChange}
                                />
                                {previewSecond && (
                                    <button className={classes.fieldWithPreviewDelete} type="button" title="Удалить фото" onClick={handleDeleteSecond}>
                                        Удалить
                                    </button>
                                )}
                                {previewSecond && <img src={previewSecond} alt="Предпросмотр второй иллюстрации" className={classes.preview} />}
                            </div>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Третья иллюстрация'}
                                    errorText={errors.thirdImage}
                                    name={'thirdImage'}
                                    type={'file'}
                                    ref={thirdFileInputRef}
                                    onChange={handleThirdFileChange}
                                />
                                {previewThird && (
                                    <button className={classes.fieldWithPreviewDelete} type="button" title="Удалить фото" onClick={handleDeleteThird}>
                                        Удалить
                                    </button>
                                )}
                                {previewThird && <img src={previewThird} alt="Предпросмотр третьей иллюстрации" className={classes.preview} />}
                            </div>
                        </div>
                        <div className={`${classes.fieldWithPreview}`}>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Четвёртая иллюстрация'}
                                    errorText={errors.fourthImage}
                                    name={'fourthImage'}
                                    type={'file'}
                                    ref={fourthFileInputRef}
                                    onChange={handleFourthFileChange}
                                />
                                {previewFourth && (
                                    <button className={classes.fieldWithPreviewDelete} type="button" title="Удалить фото" onClick={handleDeleteFourth}>
                                        Удалить
                                    </button>
                                )}
                                {previewFourth && <img src={previewFourth} alt="Предпросмотр четвёртой иллюстрации" className={classes.preview} />}
                            </div>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Пятая иллюстрация'}
                                    errorText={errors.fifthImage}
                                    name={'fifthImage'}
                                    type={'file'}
                                    ref={fifthFileInputRef}
                                    onChange={handleFifthFileChange}
                                />
                                {previewFifth && (
                                    <button className={classes.fieldWithPreviewDelete} type="button" title="Удалить фото" onClick={handleDeleteFifth}>
                                        Удалить
                                    </button>
                                )}
                                {previewFifth && <img src={previewFifth} alt="Предпросмотр пятой иллюстрации" className={classes.preview} />}
                            </div>
                            <div className={`${classes.fieldWithPreviewWrapper}`}>
                                <Input
                                    label={'Шестая иллюстрация'}
                                    errorText={errors.sixthImage}
                                    name={'sixthImage'}
                                    type={'file'}
                                    ref={sixthFileInputRef}
                                    onChange={handleSixthFileChange}
                                />
                                {previewSixth && (
                                    <button className={classes.fieldWithPreviewDelete} type="button" title="Удалить фото" onClick={handleDeleteSixth}>
                                        Удалить
                                    </button>
                                )}
                                {previewSixth && <img src={previewSixth} alt="Предпросмотр шестой иллюстрации" className={classes.preview} />}
                            </div>
                        </div>

                        <div className={classes.container}>
                            <Input
                                label={'Цена'}
                                errorText={errors.price}
                                name={'price'}
                                type={'number'}
                                value={input.price || ''}
                                onChange={handleChange}
                                required
                                placeholder={'Укажите цену товара'}
                            />

                            <ProductFormSelect
                                label={'Тип акции'}
                                defaultOptionText={'Укажите тип акции'}
                                isDisabled={false}
                                options={promoTypes}
                                onSelect={handleChangeSelect}
                                value={input.promoType.name || ''}
                                field={'promoType'}
                            />

                            <Input
                                label={'Скидка в %'}
                                errorText={errors.discount}
                                name={'discount'}
                                type={'number'}
                                max={MAX_DISCOUNT}
                                value={input.discount || ''}
                                onChange={handleChangeDisc}
                                placeholder={'Размер скидки'}
                            />

                            <Input
                                label={'Скидка в % для сотрудников'}
                                errorText={errors.specialDiscount}
                                name={'specialDiscount'}
                                type={'number'}
                                max={MAX_SPECIAL_DISCOUNT}
                                value={input.specialDiscount || ''}
                                onChange={handleChangeDisc}
                                placeholder={'Размер скидки для сотрудников'}
                            />
                        </div>

                        <div className={classes.container}>
                            <Input
                                label={'Вес нетто, кг'}
                                errorText={errors.weight}
                                name={'weight'}
                                type={'number'}
                                value={input.weight || ''}
                                onChange={handleChangeDim}
                                placeholder={'Вес товара, кг'}
                            />

                            <Input
                                label={'Вес брутто, кг'}
                                errorText={errors.grossWeight}
                                name={'grossWeight'}
                                type={'number'}
                                value={input.grossWeight || ''}
                                onChange={handleChangeDim}
                                required
                                placeholder={'Вес товара в упаковке, кг'}
                            />

                            <Input
                                label={'Длина, см'}
                                errorText={errors.length}
                                name={'length'}
                                type={'number'}
                                value={input.length || ''}
                                onChange={handleChangeDim}
                                required
                                min={0}
                                max={5000}
                                placeholder={'Длина товара, см'}
                            />
                            <Input
                                label={'Ширина, см'}
                                errorText={errors.width}
                                name={'width'}
                                type={'number'}
                                value={input.width || ''}
                                onChange={handleChangeDim}
                                required
                                min={0}
                                max={5000}
                                placeholder={'Ширина товара, см'}
                            />
                            <Input
                                label={'Высота, см'}
                                errorText={errors.height}
                                name={'height'}
                                type={'number'}
                                value={input.height || ''}
                                onChange={handleChangeDim}
                                required
                                min={0}
                                max={5000}
                                placeholder={'Высота товара, см'}
                            />

                            <ProductFormSelect
                                label={'Приоритет'}
                                title={'чем меньше значение, тем выше товар в выдаче\nзначение по умолчанию: 10'}
                                defaultOptionText={'По умолчанию (10)'}
                                isDisabled={false}
                                options={priorityOptions}
                                required={false}
                                onSelect={handlePrioritySelect}
                                value={input.priority}
                                errorText={errors.priority}
                                field={'priority'}
                            />
                        </div>

                        <div className={classes.action}>
                            <Button mode={'secondary'} onClick={handleClose}>
                                Отмена
                            </Button>
                            <Button mode={'primary'} type={'submit'}>
                                Сохранить
                            </Button>
                        </div>
                    </>
                </Form>
            </Card>
        </div>
    );
};

export default ProductForm;
